import { memo, useState, useCallback } from "react";
import { useSelection } from "../contexts/SelectionContext";
import { Card } from "../types";
import {
  Circle,
  CheckCircle,
  Sparkles,
  Gauge,
  Calendar,
  MapPin,
  Eye,
} from "lucide-react";
import { formatPrice } from "@/features/vision-360/utils/formatPrice";
import { getScoreIndicator } from "@/features/vision-360/utils/getScoreIndicator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScoreDetails, ScoreDetailsModal } from "./score-details-modal";
import {
  getCardDisplayDays,
  getCardUpdateDisplay,
  hasCardUpdateInfo,
} from "@/features/vision-360/utils/card-update-display";
import { Button } from "@/components/ui/button";
import { UpdatedPrivateOfficeBadge } from "@/features/private-room/list/utils/updated-private-office-badge";
import { ConvertStatusById } from "@/utils/convert-status-to-name/convert-status-to-name";

interface Vision360ListItemProps {
  card: Card;
  onOpenDrawer: (card: Card) => void;
  showSelectButton?: boolean;
  showUpdateTime?: boolean;
  onCenterOnMap?: (card: Card) => void;
  showCenterOnMapButton?: boolean;
}

export const Vision360ListItem = memo(function Vision360ListItem({
  card,
  onOpenDrawer,
  showSelectButton = true,
  showUpdateTime = false,
  onCenterOnMap,
  showCenterOnMapButton = false,
}: Vision360ListItemProps) {
  const [scoreDetails, setScoreDetails] = useState<ScoreDetails | null>(null);
  const { isSelected, toggleSelection } = useSelection();
  const selected = isSelected(card.id);

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleSelection(card.id);
  };

  const handleOpenDrawer = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenDrawer(card);
  };

  const handleCenterOnMap = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onCenterOnMap && card.coords?.lat && card.coords?.lng) {
        onCenterOnMap(card);
      }
    },
    [onCenterOnMap, card],
  );

  const getStatusIndicatorColor = (status: number) => {
    const statusInfo = ConvertStatusById({
      status,
      type: "STATUS_PRIVATE_OFFICE",
    });
    if (!statusInfo?.badge_color) return "#6b7280";

    const colorMap: Record<string, string> = {
      green_400: "#22c55e", // Disponível
      green_300: "#4ade80", // Disponível sem TP
      red_400: "#ef4444", // Indisponível / Removida
      red_300: "#f87171", // Indisponível sem TP
      yellow_400: "#facc15", // Ocupado com cliente Woba
      orange_400: "#f97316", // Reservado
    };

    return colorMap[statusInfo.badge_color] || "#6b7280";
  };

  const getStatusText = (status: number) => {
    const statusInfo = ConvertStatusById({
      status,
      type: "STATUS_PRIVATE_OFFICE",
    });
    return statusInfo?.title || "Status desconhecido";
  };

  const scoreConfig = getScoreIndicator(card.match_percentage);
  const HIDE_BTS_ITEMS = true;

  return (
    <>
      <div className="border-b border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer group">
        <div className="px-3 py-2">
          <div className="flex items-center gap-3">
            {showSelectButton && (
              <div className="flex-shrink-0 w-8 flex justify-center">
                <button
                  onClick={handleSelect}
                  className="hover:scale-110 transition cursor-pointer"
                  aria-label={selected ? "Desmarcar" : "Selecionar"}
                >
                  {selected ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <Circle className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="mb-1">
                <div className="flex items-center gap-2">
                  <h3
                    className="font-semibold text-base truncate"
                    title={card.title}
                  >
                    {card.title}
                  </h3>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: getStatusIndicatorColor(
                            card?.status || 0,
                          ),
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-black text-white">
                      {getStatusText(card?.status || 0)}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-600">
                <span className="truncate font-medium" title={card.space.name}>
                  {card.space.name}
                </span>
                <span className="truncate" title={card.address}>
                  {card.address}
                </span>
              </div>
            </div>

            <div className="hidden md:flex flex-col items-end text-xs text-gray-700 min-w-0 w-28">
              <div className="flex items-center gap-1">
                <span className="font-medium">{card?.capacity}</span>
                <span className="text-gray-500">pessoas</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="font-medium">
                  {card?.size ? `${card.size}m²` : ""}
                </span>
              </div>
            </div>

            <div className="hidden lg:flex flex-col items-end text-xs min-w-0 w-20">
              <div className="font-semibold text-sm text-gray-900">
                {Math.ceil(card?.credits || 0)}
              </div>
              <div className="text-gray-500">créditos</div>
            </div>

            {!HIDE_BTS_ITEMS && (
              <div className="hidden xl:flex flex-col items-end text-xs text-gray-600 min-w-0 w-28">
                <span
                  title={`Aluguel: ${formatPrice(card?.pricing?.base_rent_per_sqm)}`}
                >
                  {formatPrice(card?.pricing?.base_rent_per_sqm, "/m²")}
                </span>
                <span className="text-gray-400">aluguel</span>
              </div>
            )}

            {showUpdateTime && (
              <div className="hidden lg:flex flex-col items-end text-xs text-gray-500 min-w-0 w-20">
                <Calendar className="h-3 w-3 text-gray-400 mb-1" />
                <span className="truncate">
                  {getCardUpdateDisplay(card).displayText}
                </span>
              </div>
            )}

            <div className="w-24 flex flex-col items-start justify-center gap-1 flex-shrink-0 py-1">
              {hasCardUpdateInfo(card) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="cursor-pointer">
                      <UpdatedPrivateOfficeBadge
                        days={getCardDisplayDays(card)}
                        date={new Date(card?.updated_at || "")}
                        dateAcknowledge={undefined}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-black text-white">
                    Atualizado {getCardUpdateDisplay(card, "há ").displayText}
                  </TooltipContent>
                </Tooltip>
              )}
              <div className="flex items-center gap-1">
                {card.score && (
                  <Tooltip>
                    <TooltipTrigger
                      asChild
                      onClick={() =>
                        setScoreDetails({
                          scoreDetails: card.score ?? [],
                          details: card.score_details ?? null,
                        })
                      }
                    >
                      <div
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold cursor-pointer"
                        style={{ backgroundColor: scoreConfig, color: "#222" }}
                      >
                        <Gauge className="h-3 w-3" style={{ color: "#222" }} />
                        <span>{Number(card.match_percentage) || 0}%</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-black text-white">
                      Clique para ver mais detalhes do score
                    </TooltipContent>
                  </Tooltip>
                )}
                {card.recommendation_ai && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold"
                        style={{ background: "#F9E10D", color: "#222" }}
                      >
                        <Sparkles
                          className="h-3 w-3"
                          style={{ color: "#222" }}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-black text-white">
                      Recomendado pela IA
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleOpenDrawer}
                    className="h-8 w-8 p-0"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">Ver detalhes</TooltipContent>
              </Tooltip>

              {showCenterOnMapButton &&
                onCenterOnMap &&
                card.coords?.lat &&
                card.coords?.lng && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCenterOnMap}
                        className="h-8 w-8 p-0"
                      >
                        <MapPin className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      Centralizar no mapa
                    </TooltipContent>
                  </Tooltip>
                )}
            </div>
          </div>

          <div className="md:hidden mt-2 flex flex-wrap gap-3 text-xs text-gray-600">
            <span>
              <span className="font-medium">{card?.capacity}</span> pessoas
            </span>
            <span>
              <span className="font-medium">
                {card?.size ? `${card.size}m²` : ""}
              </span>
            </span>
            <span>
              <span className="font-medium">
                {Math.ceil(card?.credits || 0)}
              </span>{" "}
              créditos
            </span>
            {showUpdateTime && (
              <span>
                Atualizado {getCardUpdateDisplay(card, "há ").displayText}
              </span>
            )}
          </div>
        </div>
      </div>
      {scoreDetails && (
        <ScoreDetailsModal
          scoreDetails={scoreDetails}
          match={Number(card.match_percentage)}
          handleClose={() => setScoreDetails(null)}
        />
      )}
    </>
  );
});
